import { Button, CircularProgress, Divider, Grid, Typography, Box, Paper, IconButton, Avatar, Stack, Chip } from "@mui/material";
import { Close, PublicRounded, SecurityRounded, HistoryRounded, LinkRounded, CodeRounded, UserActionRounded, BadgeRounded } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useQuery } from "react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import moment from "moment";

// Identity Providers Details View Component
const IdentityProvidersDetails = ({ handleViewModalClose, identityProviderId }) => {
    const { enqueueSnackbar } = useSnackbar();
    const axiosPrivate = useAxiosPrivate();

    const { isLoading, data, isFetching, error } = useQuery(["provider_detail", identityProviderId], () => {
        return axiosPrivate.get(`/identityProviders/${identityProviderId}`);
    }, { refetchOnWindowFocus: false });

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error?.response?.data?.error?.message || error?.message || "Network Error!", {
                variant: "error",
                autoHideDuration: 2000
            });
        }
    }, [enqueueSnackbar, error]);

    const provider = data?.data?.data;

    return (
        <Grid container sx={{ minHeight: "100vh", justifyContent: "center", alignItems: { xs: "flex-end", sm: "center" }, p: { xs: 0, sm: 2 } }}>
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', sm: '600px' },
                    borderRadius: { xs: '24px 24px 0 0', sm: 6 },
                    boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.15)",
                    overflow: 'hidden',
                    position: 'relative',
                    maxHeight: '95vh',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <IconButton
                    onClick={handleViewModalClose}
                    sx={{ position: 'absolute', right: 16, top: 16, color: 'text.disabled', zIndex: 1 }}
                >
                    <Close />
                </IconButton>

                <Box sx={{ p: { xs: 3, sm: 4 }, overflowY: 'auto' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                        <Avatar
                            src={provider?.logo_uri}
                            sx={{
                                width: 80,
                                height: 80,
                                bgcolor: 'primary.lighter',
                                boxShadow: '0 8px 25px rgba(255,170,0,0.2)',
                                mb: 2,
                                borderRadius: 3
                            }}
                        >
                            <PublicRounded sx={{ fontSize: 40, color: 'primary.main' }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: 'secondary.main', letterSpacing: '-0.5px' }}>
                            {provider?.name}
                        </Typography>
                        <Chip
                            label="OIDC Provider"
                            size="small"
                            sx={{ mt: 1, fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', bgcolor: 'rgba(0,0,0,0.05)' }}
                        />
                    </Box>

                    {isLoading || isFetching ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : (
                        <Stack spacing={3}>
                            <DetailSection title="Registration Details">
                                <DetailItem icon={<BadgeRounded fontSize="small" />} label="Provider Name" value={provider?.name} />
                                <DetailItem icon={<HistoryRounded fontSize="small" />} label="Created On" value={moment(provider?.created_at).format("LLLL")} />
                            </DetailSection>

                            <DetailSection title="OAuth 2.0 Credentials">
                                <DetailItem icon={<CodeRounded fontSize="small" />} label="Client ID" value={provider?.client_id} copyable />
                                <DetailItem icon={<SecurityRounded fontSize="small" />} label="Client Secret" value="••••••••••••••••" colored />
                            </DetailSection>

                            <DetailSection title="Endpoints & Redirection">
                                <DetailItem icon={<LinkRounded fontSize="small" />} label="Authorization URI" value={provider?.authorization_uri} multiline />
                                <DetailItem icon={<LinkRounded fontSize="small" />} label="Token Endpoint" value={provider?.token_endpoint_uri} multiline />
                                <DetailItem icon={<LinkRounded fontSize="small" />} label="User Info Endpoint" value={provider?.user_info_endpoint_uri} multiline />
                                <DetailItem icon={<LinkRounded fontSize="small" />} label="Redirect URI" value={provider?.redirect_uri} multiline />
                            </DetailSection>
                        </Stack>
                    )}

                    <Box sx={{ mt: 4 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ borderRadius: 3, py: 1.5, fontWeight: 700, textTransform: 'none', bgcolor: 'secondary.main' }}
                            onClick={handleViewModalClose}
                        >
                            Close Details
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Grid>
    );
};

function DetailSection({ title, children }) {
    return (
        <Box>
            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', mb: 1, display: 'block', letterSpacing: '1px' }}>
                {title}
            </Typography>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 4, border: '1px solid rgba(0,0,0,0.03)' }}>
                <Stack spacing={2}>{children}</Stack>
            </Paper>
        </Box>
    );
}

function DetailItem({ icon, label, value, colored, multiline, copyable }) {
    return (
        <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{ color: 'primary.main', mt: 0.5, display: 'flex' }}>{icon}</Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>{label}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 700,
                            color: colored ? 'primary.main' : 'secondary.main',
                            whiteSpace: multiline ? 'pre-wrap' : 'normal',
                            wordBreak: 'break-all',
                            flex: 1
                        }}
                    >
                        {value || 'N/A'}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default IdentityProvidersDetails;